// frontend/src/Chatbot.js
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import './Chatbot.css';
import CalendarBubble from './CalendarBubble'; // Optional inline calendar component

function TypingIndicator() {
  return (
    <div className="typing-indicator">
      <div className="typing-indicator-dot" />
      <div className="typing-indicator-dot" />
      <div className="typing-indicator-dot" />
    </div>
  );
}

// Chatbot expects these props from App.js
export default function Chatbot({ websiteDomain, userId, sessionId }) {
  // state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [chatConfig, setChatConfig] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [showCalendarBubble, setShowCalendarBubble] = useState(false);
  const [currentMeetingType, setCurrentMeetingType] = useState(null);

  // refs
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const attemptRef = useRef(false); // tracks if we've tried connecting

  // derived flags
  const readyForChat = Boolean(websiteDomain && userId && sessionId);
  const isDisabled   = !readyForChat || !isConnected;

  // fetch config once
  useEffect(() => {
    axios.get('/api/chatbot/config')
      .then(res => setChatConfig(res.data))
      .catch(err => console.error('Chatbot: Error fetching chat config:', err));
  }, []);

  // welcome message & clear on domain change
  useEffect(() => {
    if (websiteDomain && chatConfig && messages.length === 0) {
      setMessages([{ sender: 'bot', type: 'welcome', text: chatConfig.welcome_message }]);
    }
    if (!websiteDomain) {
      setMessages([]);
      setInput('');
      setShowCalendarBubble(false);
    }
  }, [websiteDomain, chatConfig]);

  // auto-scroll on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [messages.length]);

  // websocket connection
  useEffect(() => {
    // Early-exit: only attempt WS when all IDs are present
    if (!readyForChat) {
      attemptRef.current = false;
      if (wsRef.current) {
        wsRef.current.close(1000, 'Configuration changed');
        wsRef.current = null;
      }
      setIsConnected(false);
      return;
    }

    // avoid duplicate
    const existing = wsRef.current;
    if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
      return;
    }

    // mark that we're about to connect
    attemptRef.current = true;

    const proto   = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    const WS_BASE = BACKEND ? BACKEND.replace(/^http/, 'ws') : `${proto}://${window.location.host}`;
    const wsUrl   = `${WS_BASE}/ws/chat?session_id=${encodeURIComponent(sessionId)}` +
                    `&user_id=${encodeURIComponent(userId)}` +
                    `&domain=${encodeURIComponent(websiteDomain)}`;

    console.log('Chatbot: Attempting WS connect:', wsUrl);
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('Chatbot: WS opened');
      setIsConnected(true);
      // on successful open, clear attempt flag so errors after that are genuine disconnects
      attemptRef.current = false;
    };

    ws.onmessage = event => {
      setIsBotTyping(false);
      let msg;
      try {
        msg = JSON.parse(event.data);
        if (msg.sender === 'system') return;
        if (!msg.sender) msg.sender = 'bot';
      } catch {
        msg = { sender: 'bot', type: 'text', text: event.data };
      }
      if (msg.sender === 'bot' && msg.type === 'rich' && msg.mode === 'date_time_picker') {
        setCurrentMeetingType(msg.meeting_type);
        setShowCalendarBubble(true);
      }
      setMessages(prev => [...prev, msg]);
    };

    ws.onerror = error => {
      if (!attemptRef.current) return; // ignore errors before first open
      console.error('Chatbot: WS error', error);
      setIsConnected(false);
      setMessages(prev => [...prev, { sender: 'bot', type: 'error', text: 'Chat connection error.' }]);
      if (wsRef.current === ws) wsRef.current = null;
    };

    ws.onclose = event => {
      console.log('Chatbot: WS closed', event.code, event.reason);
      setIsConnected(false);
      if (event.code !== 1000 && !attemptRef.current) {
        // genuine disconnect after open
        setMessages(prev => [...prev, {
          sender: 'bot', type: 'info',
          text: `Chat disconnected${event.reason ? `: ${event.reason}` : '.'}`
        }]);
      }
      if (wsRef.current === ws) wsRef.current = null;
    };

    return () => {
      attemptRef.current = false;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        ws.close(1000, 'Cleanup');
      }
      wsRef.current = null;
      setIsConnected(false);
    };
  }, [readyForChat, websiteDomain, userId, sessionId]);

  // send helper
  const send = payload => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(payload);
      setIsBotTyping(true);
    }
  };

  // handlers
  const handleButtonClick = action => send(`/${action}`);

  const handleButtonClickFromMessage = value => {
    setMessages(prev => [...prev, { sender: 'user', type: 'button_click', text: value }]);
    send(value);
  };

  const handleSend = () => {
    if (!input.trim() || isDisabled) return;
    const text = input.trim();
    setMessages(prev => [...prev, { sender: 'user', type: 'text', text }]);
    setInput('');
    send(text);
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !isDisabled && !showCalendarBubble) {
      handleSend();
    }
  };

  const handleCalendarConfirm = isoString => {
    setShowCalendarBubble(false);
    setCurrentMeetingType(null);
    try {
      const dt = new Date(isoString.replace('T', ' '));
      const formatted = dt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
      setMessages(prev => [...prev, { sender: 'user', type: 'text', text: `Selected: ${formatted}` }]);
    } catch {
      setMessages(prev => [...prev, { sender: 'user', type: 'text', text: 'Selected date/time submitted' }]);
    }
    send(isoString);
  };

  const handleCalendarCancel = () => {
    setShowCalendarBubble(false);
    setCurrentMeetingType(null);
    setMessages(prev => [...prev, { sender: 'user', type: 'text', text: '(Cancelled time selection)' }]);
  };

  return (
    <div className="chatbot-container">
      <div className={`chatbot ${isDisabled ? 'disabled' : ''}`}>
        <div className="chatbot-header">
          <h2>Demo Chatbot</h2>
          <h4>{websiteDomain || 'No website active'}</h4>
        </div>

        <div className="chatbot-messages" ref={messagesEndRef}>
          {messages.map((msg, i) => (
            <div key={i} className={`chatbot-message ${msg.sender}`}>
              <div>{msg.text}</div>
              {msg.buttons && msg.sender === 'bot' && (
                <div className="chatbot-buttons">
                  {msg.buttons.map((b, j) => (
                    <button key={j} onClick={() => handleButtonClickFromMessage(b.value)}>{b.label}</button>
                  ))}
                </div>
              )}
            </div>
          ))}

          {isBotTyping && (
            <div className="chatbot-message bot typing">
              <TypingIndicator />
            </div>
          )}

          {showCalendarBubble && currentMeetingType && (
            <div className="chatbot-message bot calendar-bubble">
              <CalendarBubble
                meetingType={currentMeetingType}
                onConfirm={handleCalendarConfirm}
                onCancel={handleCalendarCancel}
              />
            </div>
          )}
        </div>

        <div className="chatbot-input">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isDisabled ? 'Add a website to start chat' : 'Type your message...'}
            disabled={isDisabled || showCalendarBubble}
          />
          <button onClick={handleSend} disabled={isDisabled || showCalendarBubble || !input.trim()}>Send</button>
        </div>

        {isDisabled && (
          <div className="chatbot-overlay">
            <span>{websiteDomain ? 'Connecting chat...' : 'Please add a website URL...'}</span>
          </div>
        )}

        {!isDisabled && !showCalendarBubble && chatConfig?.buttons && (
          <div className="chatbot-actions">
            {chatConfig.buttons.map((btn, idx) => (
              <button key={idx} onClick={() => handleButtonClick(btn.action)}>{btn.label}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
